import type { FormEvent } from 'react';
import { useCallback } from 'react';
import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'sonner';

import { validateEmail } from '@/lib/auth/constants';
import { Button } from '@/lib/base/buttons/button';
import { HintText, useFormValidation } from '@/lib/base/forms';
import { Input } from '@/lib/base/forms/input/input';
import { sanitizeError } from '@/lib/utils/errors';

import FormErrors from '../../components/FormErrors';
import AuthLayout from '../../layouts/AuthLayout';
import { requestPasswordReset } from '../../lib/allauth';
import type { AllauthResponse } from '../../types/allauth';

const EMAIL_FIELDS = {
  email: {
    required: true,
    validate: (v: unknown): string | undefined => validateEmail(v),
  },
} as const;

export default function RequestPasswordReset() {
  const [email, setEmail] = useState('');
  const [response, setResponse] = useState<{
    fetching: boolean;
    content: AllauthResponse | null;
  }>({ fetching: false, content: null });
  const { getFieldProps, touchAll, isValid } = useFormValidation({
    fields: EMAIL_FIELDS,
    values: { email },
  });
  const emailField = getFieldProps('email');

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      touchAll();
      if (!isValid) {
        return;
      }
      setResponse({ ...response, fetching: true });
      requestPasswordReset(email)
        .then(content => {
          setResponse(r => {
            return { ...r, content };
          });
        })
        .catch(e => {
          console.error(e);
          toast.error(sanitizeError(e));
        })
        .then(() => {
          setResponse(r => {
            return { ...r, fetching: false };
          });
        });
    },
    [touchAll, isValid, email, response]
  );

  if (response.content?.status === 401) {
    return <Navigate to='/account/password/reset/confirm' />;
  }
  if (response.content?.status === 200) {
    return (
      <AuthLayout title='Check your email'>
        <div className='flex flex-col gap-8 text-center'>
          <div className='flex flex-col items-center gap-4'>
            <div className='w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center'>
              <svg
                className='w-8 h-8 text-green-600 dark:text-green-400'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                />
              </svg>
            </div>
            <div className='flex flex-col gap-3'>
              <h1 className='text-3xl font-semibold text-fg-brand-primary md:text-4xl'>
                Check your email
              </h1>
              <p className='text-base text-gray-600 dark:text-gray-400'>
                We've sent a password reset link to your email address.
              </p>
            </div>
          </div>

          <div className='flex flex-col gap-4'>
            <Link to='/account/login' className='w-full'>
              <Button size='md' className='w-full'>
                Back to sign in
              </Button>
            </Link>
            <p className='text-sm text-gray-500 dark:text-gray-400'>
              Didn't receive the email? Check your spam folder.
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title='Reset your password'>
      <div className='flex flex-col gap-8'>
        {/* Header */}
        <div className='flex flex-col gap-3'>
          <h1 className='text-3xl font-semibold text-fg-brand-primary md:text-4xl'>
            Reset your password
          </h1>
          <p className='text-base text-gray-600 dark:text-gray-400'>
            Enter your email and we'll send you a reset link.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} noValidate className='flex flex-col gap-6'>
          <FormErrors errors={response.content?.errors} />

          <div>
            <label
              className='block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5'
              htmlFor='email'
            >
              Email
            </label>
            <Input
              type='email'
              id='email'
              placeholder='Enter your email'
              isRequired
              onChange={setEmail}
              value={email}
              name='email'
              onBlur={emailField.onBlur}
              isInvalid={emailField.showError}
              showError={emailField.showError}
            />
            {emailField.showError && emailField.error && (
              <HintText isInvalid showError className='mt-1.5'>
                {emailField.error}
              </HintText>
            )}
            <FormErrors param='email' errors={response.content?.errors} />
          </div>

          <div className='flex flex-col gap-4'>
            <Button
              type='submit'
              isLoading={response.fetching}
              showTextWhileLoading={true}
              className='w-full'
            >
              {response.fetching ? 'Sending reset link…' : 'Send reset link'}
            </Button>

            <Link to='/account/login' className='w-full'>
              <Button color='secondary' className='w-full'>
                Back to sign in
              </Button>
            </Link>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
}
